import React, { useState } from 'react';
import { detectVideoUrl, getPlatformDisplayName } from '../utils/videoUtils';

const VideoEmbed = ({ url, className = '' }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  
  if (!url) return null;
  
  const videoInfo = detectVideoUrl(url);

  // If it's not a video URL, render as a regular link
  if (!videoInfo) {
    // Check if it's an image URL
    const isImage = /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(url);
    
    if (isImage) {
      return (
        <div className={`mt-3 ${className}`}>
          <img
            src={url}
            alt="Shared image"
            className="w-full max-h-96 object-cover rounded-lg border border-gray-200"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
          <div style={{ display: 'none' }} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
            <p className="text-sm text-gray-600">Unable to load image</p>
            <a 
              href={url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 text-sm underline"
            >
              View original link
            </a>
          </div>
        </div>
      );
    }

    // Regular link
    return (
      <div className={`mt-3 ${className}`}>
        <a 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-sm underline"
        >
          <span>🔗</span>
          <span>View Link</span>
        </a>
      </div>
    );
  }

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  if (hasError) {
    return (
      <div className={`mt-3 ${className}`}>
        <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
          <div className="flex items-center space-x-2 mb-2">
            <span className="text-red-500">⚠️</span>
            <span className="text-sm font-medium text-gray-700">
              Unable to load {getPlatformDisplayName(videoInfo.platform)} video
            </span>
          </div>
          <a 
            href={videoInfo.originalUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 text-sm underline"
          >
            Watch on {getPlatformDisplayName(videoInfo.platform)}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className={`mt-3 ${className}`}>
      <div className="relative">
        {/* Video Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">
              📹 {getPlatformDisplayName(videoInfo.platform)} Video
            </span>
          </div>
          <a 
            href={videoInfo.originalUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            Open in {getPlatformDisplayName(videoInfo.platform)}
          </a>
        </div>

        {/* Video Embed */}
        <div 
          className="relative aspect-video bg-black rounded-lg overflow-hidden"
          onClick={(e) => {
            // Prevent any parent click handlers on mobile
            e.stopPropagation();
          }}
          onTouchStart={(e) => {
            // Prevent default touch behavior that might open external app
            e.stopPropagation();
          }}
        >
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 pointer-events-none">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}
          
          {/* Mobile-friendly iframe wrapper */}
          <div className="w-full h-full">
            <iframe
              key={`${videoInfo.videoId}-${Date.now()}`}
              src={videoInfo.embedUrl}
              title={`${getPlatformDisplayName(videoInfo.platform)} Video`}
              className="w-full h-full"
              frameBorder="0"
              allowFullScreen
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
              loading="lazy"
              style={{ border: 0, position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            />
          </div>
        </div>

        {/* Video Info */}
        <div className="mt-2 text-xs text-gray-500">
          Shared from {getPlatformDisplayName(videoInfo.platform)}
        </div>
      </div>
    </div>
  );
};

export default VideoEmbed;